'use client';

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import type { EdgeIframeCheckoutParams } from '../buildEdgeIframeCheckoutUrl';
import { buildCheckoutEventProperties, createCheckoutSessionId } from '../checkoutTracking';
import {
  type EdgeCheckoutOpenOptions,
  registerEdgeCheckoutGlobal,
  subscribeEdgeCheckout,
} from '../edgeCheckoutController';
import type { IamOrderCompleteDetail, PremiumCheckoutCloseResult } from './PremiumCheckoutIframeModal';
import { EdgeCheckoutModal } from './EdgeCheckoutModal';

export const EdgeCheckoutHost: React.FC = () => {
  const router = useRouter();
  const session = useContext(SessionContext);
  const onUnlockRef = useRef<(() => void) | undefined>(undefined);
  const lastCheckoutParamsRef = useRef<EdgeIframeCheckoutParams | null>(null);
  const openedAtRef = useRef<number | null>(null);
  const [activeCheckout, setActiveCheckout] = useState<EdgeCheckoutOpenOptions | null>(null);

  const getTrackingExtras = useCallback(
    (checkout: EdgeCheckoutOpenOptions, overrides?: { amount?: number; includeDuration?: boolean }) => {
      const durationMs =
        overrides?.includeDuration && openedAtRef.current !== null ? Date.now() - openedAtRef.current : undefined;

      return {
        amount: overrides?.amount,
        checkout_mode: 'modal' as const,
        checkout_session_id: checkout.checkoutSessionId ?? createCheckoutSessionId(),
        duration_ms: durationMs,
      };
    },
    [],
  );

  const handleOpen = useCallback((options: EdgeCheckoutOpenOptions) => {
    onUnlockRef.current = options.onUnlock;
    lastCheckoutParamsRef.current = options.checkoutParams;
    openedAtRef.current = Date.now();
    setActiveCheckout({
      ...options,
      checkoutSessionId: options.checkoutSessionId ?? createCheckoutSessionId(),
    });
  }, []);

  useEffect(() => {
    const unregisterGlobal = registerEdgeCheckoutGlobal();
    const unsubscribe = subscribeEdgeCheckout(handleOpen);
    return () => {
      unsubscribe();
      unregisterGlobal();
    };
  }, [handleOpen]);

  useEffect(() => {
    if (!activeCheckout) return;
    session
      .getManager(TrackingManager)
      .trackCheckoutEvent('view', buildCheckoutEventProperties(activeCheckout, getTrackingExtras(activeCheckout)));
  }, [activeCheckout, getTrackingExtras, session]);

  const handleOrderComplete = useCallback(
    (detail: IamOrderCompleteDetail) => {
      if (!activeCheckout) return;
      const amount = typeof detail.amount === 'number' ? detail.amount : undefined;
      session
        .getManager(TrackingManager)
        .trackCheckoutEvent(
          'purchase',
          buildCheckoutEventProperties(
            activeCheckout,
            getTrackingExtras(activeCheckout, { amount, includeDuration: true }),
          ),
        );
    },
    [activeCheckout, getTrackingExtras, session],
  );

  const handleCheckoutClose = useCallback(
    (result?: PremiumCheckoutCloseResult) => {
      if (activeCheckout && !result?.completed) {
        session
          .getManager(TrackingManager)
          .trackCheckoutEvent(
            'close',
            buildCheckoutEventProperties(activeCheckout, getTrackingExtras(activeCheckout, { includeDuration: true })),
          );
      }
      openedAtRef.current = null;
      setActiveCheckout(null);
      if (!result?.completed) return;
      onUnlockRef.current?.();
      onUnlockRef.current = undefined;
      router.refresh();
    },
    [activeCheckout, getTrackingExtras, router, session],
  );

  const checkoutParams = activeCheckout?.checkoutParams ?? lastCheckoutParamsRef.current;
  if (!checkoutParams) {
    return null;
  }

  return (
    <EdgeCheckoutModal
      checkoutParams={checkoutParams}
      onClose={handleCheckoutClose}
      onOrderComplete={handleOrderComplete}
      open={!!activeCheckout}
    />
  );
};
